// Central step icon + accent map shared by Step page, Path overview, and Dashboard.
// Indexed by step_number (1-12). Phase grouping: 1-3 Foundation, 4-6 Outreach,
// 7-9 Content, 10-12 Launch.
import {
  Lightbulb, Layout, DollarSign, MessageSquare, RefreshCw, BarChart2,
  Type, FileText, PenLine, MousePointerClick, Settings2, Rocket
} from 'lucide-react';

export const STEP_META = {
  1:  { icon: Lightbulb,           gradient: 'from-amber-400 to-yellow-500',   accent: '#f59e0b' },
  2:  { icon: Layout,              gradient: 'from-teal-500 to-emerald-600',   accent: '#0e8175' },
  3:  { icon: DollarSign,          gradient: 'from-emerald-500 to-green-600',  accent: '#10b981' },
  4:  { icon: MessageSquare,       gradient: 'from-violet-500 to-purple-600',  accent: '#7c3aed' },
  5:  { icon: RefreshCw,           gradient: 'from-indigo-500 to-blue-600',    accent: '#4f46e5' },
  6:  { icon: BarChart2,           gradient: 'from-cyan-500 to-sky-600',       accent: '#0891b2' },
  7:  { icon: Type,                gradient: 'from-rose-500 to-pink-600',      accent: '#f43f5e' },
  8:  { icon: FileText,            gradient: 'from-orange-500 to-amber-600',   accent: '#f97316' },
  9:  { icon: PenLine,             gradient: 'from-teal-600 to-cyan-700',      accent: '#0e8175' },
  10: { icon: MousePointerClick,   gradient: 'from-emerald-500 to-teal-600',   accent: '#059669' },
  11: { icon: Settings2,           gradient: 'from-slate-500 to-gray-600',     accent: '#475569' },
  12: { icon: Rocket,              gradient: 'from-amber-500 to-orange-600',   accent: '#d97706' },
};

// Phase grouping. Each step number maps to one of 4 phases.
export const PHASES = [
  { id: 'foundation', label: 'Foundation', emoji: '🏗️', color: '#0e8175', steps: [1, 2, 3] },
  { id: 'outreach',   label: 'Outreach',   emoji: '📣', color: '#7c3aed', steps: [4, 5, 6] },
  { id: 'content',    label: 'Content',    emoji: '📝', color: '#f43f5e', steps: [7, 8, 9] },
  { id: 'launch',     label: 'Launch',     emoji: '🚀', color: '#d97706', steps: [10, 11, 12] },
];

export const getStepMeta = (stepNumber) =>
  STEP_META[stepNumber] || STEP_META[1];

export const getPhase = (stepNumber) =>
  PHASES.find((p) => p.steps.includes(stepNumber)) || PHASES[0];

// Short motivational lines used in the "Up Next" card on the step page.
export const getMotivationalTagline = (nextStepNumber, totalSteps = 12) => {
  if (!nextStepNumber) return '';
  const phase = getPhase(nextStepNumber);
  const pct = Math.round(((nextStepNumber - 1) / totalSteps) * 100);
  if (nextStepNumber === totalSteps) return "Last one — let's launch this thing.";
  if (pct < 25) return `Welcome to the ${phase.label} phase — the groundwork that pays you back later.`;
  if (pct < 50) return `You're rolling — ${phase.label.toLowerCase()} is where it starts to feel real.`;
  if (pct < 75) return `Halfway through — keep the momentum into the ${phase.label} phase.`;
  return `Almost at the finish — ${phase.label.toLowerCase()} is the final stretch.`;
};

// Rotating welcome subline for the dashboard, by overall completion %.
export const getDashboardSubline = (pct) => {
  if (pct >= 100) return "You did it. Time to level up to a new path.";
  if (pct >= 71)  return "Almost there. Finish strong!";
  if (pct >= 31)  return "Past the halfway point — the best part is ahead.";
  if (pct >= 1)   return "You're building momentum. Keep going!";
  return "Your first step is waiting — let's make today count.";
};
